import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, CalendarBlank, NotePencil, WhatsappLogo, FunnelSimple } from "@phosphor-icons/react";

const CRMActivities = ({ user, logout }) => {
  const [activities, setActivities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [filterUserId, setFilterUserId] = useState(""); // admin/manager filter
  const [filterType, setFilterType] = useState("");
  const [newActivity, setNewActivity] = useState({
    type: "call", description: "", contact_id: "", contact_name: "",
    created_by: user?.user_id || "", created_by_name: user?.name || ""
  });

  const canMonitorAll = ["admin", "manager", "bdm"].includes(user?.role);

  const fetchActivities = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_name: user?.name || ""
      });
      if (canMonitorAll && filterUserId) params.set("created_by", filterUserId);
      const response = await api.get(`${API}/crm/activities?${params.toString()}`);
      setActivities(response.data);
    } catch (error) { toast.error("Failed to fetch activities"); }
    finally { setLoading(false); }
  }, [user?.role, user?.name, filterUserId, canMonitorAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContactsAndUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_name: user?.name || ""
      });
      const [cRes, uRes] = await Promise.all([
        api.get(`${API}/crm/contacts?${params.toString()}`),
        canMonitorAll ? api.get(`/users`) : Promise.resolve({ data: [] })
      ]);
      setContacts(cRes.data);
      setUsers(uRes.data.filter(u => u.role !== "customer"));
    } catch (error) { console.error("Failed to fetch contacts or users"); }
  }, [user?.role, user?.name, canMonitorAll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchActivities();
    fetchContactsAndUsers();
  }, [fetchActivities, fetchContactsAndUsers]);

  const handleLog = async (e) => {
    e.preventDefault();
    try {
      const contact = contacts.find(c => c.contact_id === newActivity.contact_id);
      await api.post(`${API}/crm/activities`, {
        ...newActivity,
        contact_name: contact?.name || ""
      });
      toast.success("Activity logged");
      setShowLog(false);
      setNewActivity({ type: "call", description: "", contact_id: "", contact_name: "", created_by: user?.user_id || "", created_by_name: user?.name || "" });
      fetchActivities();
    } catch (error) { toast.error("Failed to log activity"); }
  };

  const getTypeIcon = (type) => {
    if (type === "call") return <Phone size={20} weight="fill" className="text-[#10B981]" />;
    if (type === "email") return <EnvelopeSimple size={20} weight="fill" className="text-[#002FA7]" />;
    if (type === "meeting") return <CalendarBlank size={20} weight="fill" className="text-[#F59E0B]" />;
    if (type === "whatsapp") return <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" />;
    return <NotePencil size={20} weight="fill" className="text-[#475569]" />;
  };

  const getTypeBg = (type) => {
    if (type === "call") return "bg-green-50 border-green-200";
    if (type === "email") return "bg-blue-50 border-blue-200";
    if (type === "meeting") return "bg-yellow-50 border-yellow-200";
    if (type === "whatsapp") return "bg-emerald-50 border-emerald-200";
    return "bg-slate-50 border-slate-200";
  };

  const filtered = useMemo(() => {
    if (!filterType) return activities;
    return activities.filter(a => a.type === filterType);
  }, [activities, filterType]);

  // Group stats by user for quick overview
  const statsByUser = useMemo(() => {
    const map = {};
    for (const a of activities) {
      const key = a.created_by_name || "Unknown";
      map[key] = (map[key] || 0) + 1;
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [activities]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Activity Log</h1>
              <p className="text-base leading-relaxed text-slate-700">
                {canMonitorAll ? "Monitor your team's interactions with customers" : "Track your customer interactions"}
              </p>
            </div>
            <button data-testid="log-activity-btn" onClick={() => setShowLog(!showLog)}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300">
              {showLog ? 'Cancel' : 'Log Activity'}
            </button>
          </div>

          {/* Admin/Manager filter controls */}
          <div className="mb-6 bg-white border border-slate-200 p-4 flex items-center gap-4 flex-wrap">
            <FunnelSimple size={18} weight="bold" className="text-slate-500" />
            {canMonitorAll && (
              <div className="flex items-center gap-2">
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Team Member</label>
                <select
                  data-testid="activity-user-filter"
                  value={filterUserId}
                  onChange={(e) => setFilterUserId(e.target.value)}
                  className="rounded-none border border-slate-300 text-sm p-2 outline-none min-w-[180px]"
                >
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.role.toUpperCase()}</option>)}
                </select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Type</label>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="activity-type-filter"
                className="rounded-none border border-slate-300 text-sm p-2 outline-none">
                <option value="">All Types</option>
                <option value="call">Calls</option>
                <option value="email">Email</option>
                <option value="meeting">Meetings</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="note">Notes</option>
              </select>
            </div>
            <span className="text-xs text-slate-500 ml-auto">
              Showing <span className="font-bold text-slate-700">{filtered.length}</span> activit{filtered.length === 1 ? 'y' : 'ies'}
            </span>
          </div>

          {/* Team stats quick overview (admin/manager only, no user selected) */}
          {canMonitorAll && !filterUserId && statsByUser.length > 0 && (
            <div className="bg-white border border-slate-200 p-5 mb-6">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Activity by Team Member</div>
              <div className="flex gap-3 flex-wrap">
                {statsByUser.map(([name, count]) => {
                  const u = users.find(x => x.name === name);
                  return (
                    <button key={name} data-testid={`stats-user-${name}`}
                      onClick={() => u && setFilterUserId(u.user_id)}
                      className="bg-slate-50 border border-slate-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-colors duration-300 px-4 py-2 text-left">
                      <div className="font-semibold text-sm">{name}</div>
                      <div className="text-xs text-slate-500">{count} {count === 1 ? 'activity' : 'activities'}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {showLog && (
            <div data-testid="log-activity-form" className="bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>Log New Activity</h3>
              <form onSubmit={handleLog} className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Activity Type</label>
                  <select data-testid="activity-type-select" value={newActivity.type} onChange={(e) => setNewActivity({...newActivity, type: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                    <option value="call">Phone Call</option>
                    <option value="email">Email</option>
                    <option value="meeting">Meeting</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="note">Note</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Related Contact</label>
                  <select data-testid="activity-contact-select" value={newActivity.contact_id} onChange={(e) => setNewActivity({...newActivity, contact_id: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                    <option value="">Select Contact (optional)</option>
                    {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name} - {c.company || c.email}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Description</label>
                  <textarea data-testid="activity-description-input" value={newActivity.description} onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" rows="4" required
                    placeholder="Describe the activity..." />
                </div>
                <div className="col-span-2">
                  <button data-testid="submit-activity-btn" type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300">Log Activity</button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
          ) : (
            <div data-testid="activities-timeline" className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-200"></div>
              <div className="space-y-6">
                {filtered.map((activity, index) => (
                  <div key={activity.activity_id} data-testid={`activity-item-${index}`} className="flex items-start gap-6 relative">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-none border z-10 ${getTypeBg(activity.type)}`}>
                      {getTypeIcon(activity.type)}
                    </div>
                    <div className="flex-1 bg-white border border-slate-200 rounded-none p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <span className="rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                            {activity.type}
                          </span>
                          {activity.contact_name && (
                            <span className="text-sm font-medium text-[#002FA7]">{activity.contact_name}</span>
                          )}
                          {activity.created_by_name && (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5">
                              by {activity.created_by_name}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(activity.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{activity.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              {filtered.length === 0 && (
                <div className="text-center py-12 text-slate-500 ml-16">No activities match the current filters</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMActivities;
